import { Input, LuIcon, Menu, Popover } from "@overmap-ai/blocks"
import { fieldDataMapping, type RatingField } from "@overmap-ai/forms"
import { use, useCallback } from "react"

import { FilterModifierDropdown, RemoveFilterButton } from "../../components"
import { FilterCellBadge } from "../../components/FilterCellBadge"
import { FilterCellButton } from "../../components/FilterCellButton"
import { FilterCellRoot } from "../../components/FilterCellRoot"
import { FilterRendererContext } from "../../contexts/FilterRendererContext/context"
import type { FilterDefRenderProps } from "../BaseFilterDef"
import type { RatingFieldFilterDef } from "./RatingFieldFilterDef"

interface RatingFieldFilterCellProps extends FilterDefRenderProps<RatingFieldFilterDef> {
	field: RatingField
}

export const RatingFieldFilterCell = (props: RatingFieldFilterCellProps) => {
	const { field, filterDef, filter, filterManager } = props

	const { size } = use(FilterRendererContext)

	const filterValue = filter.getFilterValue()
	const filterModifier = filter.getFilterModifier()

	const handleSelectEmpty = useCallback(() => {
		filter.setFilterValue(null)
	}, [filter])

	const getFilterValueUi = () => {
		switch (filterModifier) {
			case "equals":
			case "notEquals": {
				const equalsModifier = filterDef.modifiers.equals
				const equalsFilterValue =
					filterValue !== undefined && equalsModifier.isFilterValueValid(filterValue)
						? filterValue
						: undefined

				return (
					<Menu.Root>
						<Menu.ClickTrigger>
							<FilterCellButton className="truncate">
								{equalsFilterValue !== undefined ? (equalsFilterValue ?? "empty") : "..."}
							</FilterCellButton>
						</Menu.ClickTrigger>
						<Menu.Content size={size}>
							<Menu.Pages defaultPage="initial">
								<Menu.PageContent page="initial">
									<Menu.PageTrigger page="custom">Custom value</Menu.PageTrigger>
									<Menu.Item onSelect={handleSelectEmpty}>empty</Menu.Item>
								</Menu.PageContent>
								<Menu.PageContent page="custom">
									<Input.Root accentColor="base" size={size}>
										<Input.Field
											placeholder="Enter a rating"
											type="number"
											value={equalsFilterValue ?? ""}
											onChange={(e) => {
												const value = e.target.valueAsNumber
												filter.setFilterValue(isNaN(value) ? undefined : value)
											}}
										/>
									</Input.Root>
								</Menu.PageContent>
							</Menu.Pages>
						</Menu.Content>
					</Menu.Root>
				)
			}
			case "greaterThanOrEquals":
			case "lessThanOrEquals":
			case "greaterThan":
			case "lessThan": {
				const greaterThanModifier = filterDef.modifiers.greaterThan
				const greaterThanFitlerValue =
					filterValue !== undefined && greaterThanModifier.isFilterValueValid(filterValue)
						? filterValue
						: undefined

				return (
					<Popover.Root>
						<Popover.Trigger asChild>
							<FilterCellButton className="truncate">{greaterThanFitlerValue ?? "..."}</FilterCellButton>
						</Popover.Trigger>
						<Popover.Content size={size}>
							<Input.Root accentColor="base" size={size}>
								<Input.Field
									placeholder="Enter a rating"
									type="number"
									value={greaterThanFitlerValue ?? ""}
									onChange={(e) => {
										const value = e.target.valueAsNumber
										filter.setFilterValue(isNaN(value) ? undefined : value)
									}}
								/>
							</Input.Root>
						</Popover.Content>
					</Popover.Root>
				)
			}
			case "inRange":
			case "notInRange": {
				const inRangeModifier = filterDef.modifiers.inRange
				const rangeFilterValue =
					filterValue !== undefined && inRangeModifier.isFilterValueValid(filterValue)
						? filterValue
						: undefined

				return (
					<>
						<Popover.Root>
							<Popover.Trigger asChild>
								<FilterCellButton className="truncate">
									{rangeFilterValue?.from ?? "..."}
								</FilterCellButton>
							</Popover.Trigger>
							<Popover.Content size={size}>
								<Input.Root accentColor="base" size={size}>
									<Input.Field
										placeholder="Enter a rating"
										type="number"
										value={rangeFilterValue?.from ?? ""}
										onChange={(e) => {
											const value = e.target.valueAsNumber

											filter.setFilterValue({
												...rangeFilterValue,
												from: isNaN(value) ? undefined : value,
											})
										}}
									/>
								</Input.Root>
							</Popover.Content>
						</Popover.Root>
						<Popover.Root>
							<Popover.Trigger asChild>
								<FilterCellButton className="truncate">
									{rangeFilterValue?.to ?? "..."}
								</FilterCellButton>
							</Popover.Trigger>
							<Popover.Content size={size}>
								<Input.Root accentColor="base" size={size}>
									<Input.Field
										placeholder="Enter a rating"
										type="number"
										value={rangeFilterValue?.to ?? ""}
										onChange={(e) => {
											const value = e.target.valueAsNumber
											filter.setFilterValue({
												...rangeFilterValue,
												to: isNaN(value) ? undefined : value,
											})
										}}
									/>
								</Input.Root>
							</Popover.Content>
						</Popover.Root>
					</>
				)
			}
		}
	}

	return (
		<FilterCellRoot>
			<FilterCellBadge className="shrink-1 min-w-0">
				<LuIcon icon={fieldDataMapping[field.type].icon} />
				<span className="truncate">{field.label}</span>
			</FilterCellBadge>

			<FilterModifierDropdown filterDef={filterDef} filter={filter}>
				<FilterCellButton>
					{filterDef.getFilterModifier(filterModifier).modifier.render(filterValue)}
				</FilterCellButton>
			</FilterModifierDropdown>

			{getFilterValueUi()}

			<RemoveFilterButton filter={filter} filterManager={filterManager} />
		</FilterCellRoot>
	)
}
